import { Locale } from "next/router";

import localeConfig from "../../config/locales";

/**
 * The locale list for bundled code, typed.
 *
 * Read off `config/locales.js` rather than restated here, so this file and the
 * one Next actually routes on cannot drift. Deliberately not read off
 * `next-i18next.config.js`: that would pull `path`, the sprintf post-processor
 * and the namespace manifest into every client and edge bundle that imports
 * this module.
 */

/**
 * The six content languages as a literal tuple, for narrowing an incoming
 * string to one of them (see `FAQLocale`). `en-us` is not one of them: it is a
 * routed locale that reads `en` (`toContentLocale`, WHO-5766).
 */
export const configuredLocales = localeConfig.contentLocales;

/** One of the six languages we write content in. */
export type ConfiguredLocale = (typeof configuredLocales)[number];

/** Alias that says what the type is for at a data boundary. */
export type ContentLocale = ConfiguredLocale;

/** `/en-us`, the US market (WHO-5766). Routed only while `US_LAUNCH_ENABLED` is on. */
export const US_LOCALE = localeConfig.US_LOCALE;

/**
 * The routed locales: the six, plus `en-us` while `US_LAUNCH_ENABLED` is on.
 *
 * A fresh array each import on purpose. `router.locales` has been mutated in
 * place before now, and that shifted locales across a build worker's batch —
 * see the WHO-5021 note in scripts/verify-locales.ts.
 */
export const locales: Locale[] = [...localeConfig.locales];
export const defaultLocale = localeConfig.defaultLocale;

/**
 * The content language of a routed locale: `en-us` reads `en` (WHO-5766).
 * Every map, file or API argument keyed by language goes through this; a
 * `undefined` locale (a page outside i18n) reads the default.
 * `__tests__/i18n/content-locale-boundaries.spec.ts` fails on a new
 * language-keyed map that skips it.
 */
export const toContentLocale = (locale: string | undefined): ContentLocale => {
  const content = localeConfig.toContentLocale(locale ?? defaultLocale);

  return (configuredLocales as readonly string[]).includes(content)
    ? (content as ContentLocale)
    : defaultLocale;
};

/**
 * Whether a routed locale is the US market (`/en-us`, WHO-5766). Use it for
 * US-only behaviour (lists, banner, selling closed); it can only be true while
 * `US_LAUNCH_ENABLED` is on, since the locale is not routed otherwise.
 */
export const isUsLocale = (locale: string | undefined): boolean =>
  locale === US_LOCALE;

/** Narrowing guard for a string that may be one of the six content languages. */
export const isContentLocale = (value: unknown): value is ContentLocale =>
  (configuredLocales as readonly unknown[]).includes(value);
